<template>
  <main class="pp-page--seat-preview">
    <h1 class="title title--desktop">{{ $t('message.choose_seats', { sectionAlias: performance.sectionAlias}) }}</h1>
    <div class="pp-layout-container">
      <div class="pp-preamble">
        <h1 class="title title--mobile">{{ $t('message.choose_seats', { sectionAlias: performance.sectionAlias}) }}</h1>
        <div v-if="performance.mode == 'BA'" class="seat-preview__preamble seat-preview__preamble--best-available">
          <p v-t="'message.best_available_body'"></p>
          <button class="btn--secondary btn--best-available"
            :class="index == setSelected ? 'btn--secondary--active' : ''"
            v-for="(set, index) in bestAvailableOptions"
            :key="index"
            @click="selectSet(index)"
          >
            <span v-for="(seat, index) in set.setsReserved" :key="`seat-set--${index}`">
              {{ seat.seatRowAlias }}{{ seat.seatAlias }}
            </span>
          </button>
        </div>
        <div v-else class="seat-preview__preamble seat-preview__preamble--hot-show">
          <p v-t="'message.hot_show_body'"></p>
        </div>
      </div>
      <div class="pp-main-content">
        <seats-legend :showSeatOffered="true" />
        <panZoom
          @init="initPanzoom"
          :options="{ minZoom: 0.5, maxZoom: 1.5, fit: true }"
        >
          <div class="seat-picker__container" ref="imageContainer">
            <svg
              ref="imageViewport"
              :viewBox="'0 0 ' + performance.backgroundImageWidth + ' ' + performance.backgroundImageHeight"
            >
              <image ref="backgroundMap" :href="performance.backgroundImageSource" />
              <!-- OFfered seats array -->
              <seat-item
                v-for="(seat, index) in offeredSeats"
                :index="index"
                :key="`offered-seats--${index}--${seat.inventoryId}`"
                :seatInfo="seat"
                :offered="true"
              />
              <!-- Selected seats -->
              <seat-item
                v-for="(seat, index) in seatsSelected"
                :index="index"
                :key="`selected-seats--${index}--${seat.inventoryId}`"
                :data-cord="seat.topLeftCoordinates"
                :seatInfo="seat"
                :available="true"
                :selected="true"
                tabindex="-1"
              />
            </svg>
          </div>
        </panZoom>
        <zoom-controls @zoom="zoom" @resetPosition="resetPosition" />
        <pan-controls @pan="pan" />
        <fancy-button class="choose-seats__cta choose-seats__cta--desktop"
          :icon="['far', 'ticket-alt']"
          :text="$t('buttons.confirm_seats')"
          :disabled="setSelected == -1"
          @click.native="submitSetSelection"/>
        <div class="pp-actions">
          <div class="pp-actions__container">
            <seating-selection-info v-if="seatsSelected.length > 0"
              :seatSelectionDetails="seatSelectionDetails" />
            <fancy-button class="choose-seats__cta choose-seats__cta--mobile"
              :icon="['far', 'ticket-alt']"
              :text="$t('buttons.confirm_seats')"
              :disabled="setSelected == -1"
              @click.native="submitSetSelection"/>
          </div>
        </div>
      </div>
      <aside class="pp-sidebar">
        <seats-legend :showSeatOffered="true"/>
        <zoom-controls @zoom="zoom" @resetPosition="resetPosition" />
        <pan-controls @pan="pan" />
        <seating-selection-info v-if="seatsSelected.length > 0"
          :seatSelectionDetails="seatSelectionDetails" />
      </aside>
    </div>
  </main>
</template>

<script>
import FancyButton from "@/components/generic/FancyButton";
import SeatItem from "@/components/SeatItem";
import ZoomControls from "@/components/seating-area/ZoomControls";
import PanControls from "@/components/seating-area/PanControls";
import CanvasInteractions from "@/util/CanvasInteractions";
import SeatsLegend from "@/components/seating-area/SeatsLegend";
import SeatingSelectionInfo from "@/components/seating-area/SeatingSelectionInfo"
import { mapActions, mapState } from 'vuex';

export default {
  name: "seat-preview",
  mixins: [CanvasInteractions],
  components: {
    FancyButton,
    SeatItem,
    ZoomControls,
    PanControls,
    SeatsLegend,
    SeatingSelectionInfo
  },
  data() {
    return {
      setSelected: -1,
      seatsSelected: [],
      seatSelectionDetails: {}
    };
  },
  watch: {
    "$i18n.locale": function() {
      this.initCreated();
      this.initMounted();
    }
  },
  created() {
    this.initCreated();
  },
  async mounted() {
    await this.initMounted();
  },
  methods: {
    initCreated() {
      this.clearBackgroundImage();
    },
    async initMounted() {
      this.clearError();
      await this.retrieveSeatOptions().then(() => {
        this.updateLoading(false)
      });
      
      if (this.performance.mode == "HS") {
        // Select the initial set (there's no choice)
        this.selectSet(0);
      }

      // We want to store all inventory IDs between the two sets in case the user refreshes the page
      // meaning they'd end up at the choose-date-time screen (while still reserving these seats)
      this.initialInventoryReserveList();
    },
    async submitSetSelection() {
      this.scrollToTopOfPurchasePath();
      this.updateLoading(true);

      if (this.setSelected != -1) {
        let seatsToReserveArray = this.bestAvailableOptions[this.setSelected]["setsReserved"];

        if(this.performance.mode == "BA") {
          await this.returnNonSelectedSet();
        }

        await this.updateInventoryReservedList(seatsToReserveArray);

        this.$emit("submitSetSelection");
      } else {
        this.updateError("select_a_set");
        this.updateLoading(false);
      }
    },
    async returnNonSelectedSet() {
      let seatsToRelease = []
      
      // We want to return the set that hasn't been returned; hence preparing set[1] for release if setSelected == 0
      if(this.setSelected == 0) {
        // it's possible for BA to return only one set due to not enough seats to generate two sets
        // therefore there is no second set; and crashes the flow trying to release
        if(this.bestAvailableOptions[1] != undefined) {
          seatsToRelease = processSeatsReleaseList(this.bestAvailableOptions[1]["setsReserved"])
        }
      } else {
        seatsToRelease = processSeatsReleaseList(this.bestAvailableOptions[0]["setsReserved"])
      }

      await this.updateInventoryReservedList(seatsToRelease);
      await this.returnItemsForSelection(this.performance.productId);

      function processSeatsReleaseList(items) {
        let itemsToReturn = []

        items.forEach(item => {
          itemsToReturn.push(item.inventoryId)
        });

        return itemsToReturn
      }
    },
    selectSet(index) {
      this.setSelected = index;
      this.highlightSeats(this.bestAvailableOptions[index]);
      this.clearError();

      // Reset seatSelectionDetails on each selection
      for (var key in this.seatSelectionDetails) delete this.seatSelectionDetails[key];

      // Process seats within a set
      let seatsInOption = this.bestAvailableOptions[index]["setsReserved"]

      // We need to add each seat keyed on inventoryId, and its row+seat info
      // so we can display to the user what they've selected.
      seatsInOption.forEach((seat) => {
        this.$set(this.seatSelectionDetails, seat.inventoryId, {
          row: seat.seatRowAlias,
          seat: seat.seatAlias
        });
      });
    },
    highlightSeats(seatSet) {
      this.seatsSelected = seatSet.setsReserved;
    },
    initialInventoryReserveList() {
      let initialIdList = []

      this.bestAvailableOptions.forEach(seatList => {
        seatList.setsReserved.forEach(seat => {
          initialIdList.push(seat.inventoryId)
        });
      });

      this.updateInventoryReservedList(initialIdList);
    },
    ...mapActions([
      'updateLoading', 
      'updateError',
      'clearError',
      'updateInventoryReservedList',
      'retrieveSeatOptions', 
      'clearBackgroundImage', 
      'returnItemsForSelection', 
      'updateStage',
      'scrollToTopOfPurchasePath'
    ])
  },
  computed: {
    bestAvailableOptions() {
      return this.performance.availableOptions.setsReservedList;
    },
    offeredSeats() {
      let offeredArray = []

      // Concatenate all items within the two separate offer arrays into one
      this.bestAvailableOptions.forEach((item) => {
        offeredArray = offeredArray.concat(item.setsReserved);
      });

      return offeredArray;
    },
    ...mapState(['performance'])
  },
};
</script>
